import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Search, UserPlus, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { DepartmentScopePicker, departmentLabel } from './DepartmentScopePicker';

const PER_PAGE = 100;

const UserRow = ({ user, checked, onToggle }) => (
    <div className={`flex cursor-pointer items-center gap-3 rounded-lg p-2 transition-colors ${user.is_assigned ? 'bg-muted opacity-60' : checked ? 'bg-primary/10' : 'hover:bg-muted'}`}
        onClick={() => !user.is_assigned && onToggle(user.id)} data-testid={`assign-user-row-${user.id}`}>
        <Checkbox checked={user.is_assigned || checked} disabled={user.is_assigned} onCheckedChange={() => !user.is_assigned && onToggle(user.id)} onClick={(e) => e.stopPropagation()} />
        <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{user.name}</p>
            <p className="truncate text-sm text-muted-foreground">{user.email}</p>
        </div>
        <Badge variant="secondary" className="shrink-0 text-xs">{user.department || 'No department'}</Badge>
        {user.is_assigned && <Badge variant="outline" className="shrink-0 text-xs">Already assigned</Badge>}
    </div>
);

const AssignedList = ({ assigned, onUnassign }) => (
    <ScrollArea className="h-[380px]">
        {assigned.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">No users assigned yet</div>
        ) : (
            <div className="space-y-2">
                {assigned.map((a) => (
                    <div key={a.user_id} className="flex items-center justify-between rounded-lg bg-muted/50 p-3" data-testid={`assigned-user-${a.user_id}`}>
                        <div className="min-w-0 flex-1">
                            <p className="truncate font-medium">{a.user_name}</p>
                            <p className="truncate text-sm text-muted-foreground">{a.user_email}</p>
                        </div>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onUnassign(a.user_id)} data-testid={`unassign-user-${a.user_id}`}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                ))}
            </div>
        )}
    </ScrollArea>
);

const BulkConfirm = ({ open, onOpenChange, count, label, onConfirm }) => (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>Assign {count} {count === 1 ? 'person' : 'people'} from {label}?</AlertDialogTitle>
                <AlertDialogDescription>
                    Everyone currently in {label} who isn&apos;t assigned yet gets this course. People who join these departments later are not added automatically.
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel data-testid="assign-bulk-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onConfirm} data-testid="assign-bulk-confirm">Assign everyone</AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
);

// Assign Users window shared by every course builder (Tech Learning, Finance Training, all Course Builder modules)
export const AssignUsersDialog = ({ open, onOpenChange, builderBase, courseId, onChanged }) => {
    const { api } = useAuth();
    const apiRef = useRef(api);
    apiRef.current = api;
    const [departments, setDepartments] = useState([]);
    const [scope, setScope] = useState('all');
    const [chosen, setChosen] = useState([]);
    const [search, setSearch] = useState('');
    const [users, setUsers] = useState([]);
    const [meta, setMeta] = useState({ total: 0, unassigned: 0, page: 1, pages: 1 });
    const [selected, setSelected] = useState([]);
    const [assigned, setAssigned] = useState([]);
    const [assignedTotal, setAssignedTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [confirmBulk, setConfirmBulk] = useState(false);
    const base = `${builderBase}/courses/${courseId}`;
    const needsDepartments = scope === 'selected' && chosen.length === 0;
    const scopeLabel = scope === 'all' ? 'all departments' : chosen.map(departmentLabel).join(', ');

    const loadUsers = useCallback(async (page = 1) => {
        if (needsDepartments) { setUsers([]); setMeta({ total: 0, unassigned: 0, page: 1, pages: 1 }); return; }
        setLoading(true);
        try {
            const params = { page, per_page: PER_PAGE };
            if (search.trim()) params.search = search.trim();
            if (scope === 'selected') params.departments = chosen.join(',');
            const { data } = await apiRef.current.get(`${base}/assignable-users`, { params });
            setUsers((prev) => (page === 1 ? data.users : [...prev, ...data.users]));
            setMeta({ total: data.pagination.total, unassigned: data.unassigned_total ?? 0, page, pages: data.pagination.total_pages });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    }, [base, scope, chosen, search, needsDepartments]);

    const loadAssigned = useCallback(async () => {
        try {
            const { data } = await apiRef.current.get(`${base}/assigned-users`, { params: { per_page: 100 } });
            setAssigned(data.assignments || []);
            setAssignedTotal(data.pagination?.total ?? (data.assignments || []).length);
        } catch { /* list stays as is */ }
    }, [base]);

    useEffect(() => {
        if (!open) return;
        setSelected([]);
        loadAssigned();
        apiRef.current.get('/users/departments/list').then((r) => setDepartments(r.data.departments || [])).catch(() => setDepartments([]));
    }, [open, loadAssigned]);

    useEffect(() => {
        if (!open) return undefined;
        const t = setTimeout(() => loadUsers(1), 300);
        return () => clearTimeout(t);
    }, [open, loadUsers]);

    const refresh = () => { setSelected([]); loadUsers(1); loadAssigned(); onChanged?.(); };
    const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    const shownIds = users.filter((u) => !u.is_assigned).map((u) => u.id);
    const allShownPicked = shownIds.length > 0 && shownIds.every((id) => selected.includes(id));
    const toggleAllShown = () => setSelected((prev) => (allShownPicked ? prev.filter((id) => !shownIds.includes(id)) : [...new Set([...prev, ...shownIds])]));

    const run = async (request) => {
        setAssigning(true);
        try {
            const { data } = await request();
            toast.success(data.message);
            refresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to assign users');
        } finally {
            setAssigning(false);
        }
    };
    const assignSelected = () => run(() => apiRef.current.post(`${base}/assign`, selected));
    const assignEveryone = () => { setConfirmBulk(false); run(() => apiRef.current.post(`${base}/assign-departments`, { all_departments: scope === 'all', departments: scope === 'all' ? [] : chosen })); };
    const unassign = async (userId) => {
        try {
            await apiRef.current.delete(`${base}/assign/${userId}`);
            toast.success('User unassigned');
            refresh();
        } catch {
            toast.error('Failed to unassign user');
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="assign-users-dialog">
                <DialogHeader>
                    <DialogTitle>Assign Users to Course</DialogTitle>
                    <DialogDescription>Choose all departments or only selected ones, then pick people or assign everyone shown</DialogDescription>
                </DialogHeader>
                <Tabs defaultValue="assign">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="assign" data-testid="assign-tab-new">Assign New</TabsTrigger>
                        <TabsTrigger value="assigned" data-testid="assign-tab-assigned">Currently Assigned ({assignedTotal})</TabsTrigger>
                    </TabsList>
                    <TabsContent value="assign" className="mt-4 space-y-3">
                        <DepartmentScopePicker departments={departments} scope={scope} onScopeChange={setScope} chosen={chosen} onChosenChange={setChosen} />
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input placeholder="Search users by name or email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="assign-user-search" />
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                            <label className="flex cursor-pointer items-center gap-2" data-testid="assign-select-all-shown">
                                <Checkbox checked={allShownPicked} onCheckedChange={toggleAllShown} disabled={shownIds.length === 0} />
                                Select all shown
                            </label>
                            <span className="text-muted-foreground" data-testid="assign-user-count">
                                Showing {users.length} of {meta.total} · {meta.unassigned} in {scope === 'all' ? 'all departments' : 'these departments'} not assigned yet
                            </span>
                        </div>
                        <ScrollArea className="h-[260px] rounded-lg border p-2">
                            {loading && users.length === 0 ? (
                                <div className="flex h-[240px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
                            ) : users.length === 0 ? (
                                <div className="py-8 text-center text-muted-foreground" data-testid="assign-no-users">
                                    {needsDepartments ? 'Pick at least one department to see its users' : 'No users found'}
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {users.map((u) => <UserRow key={u.id} user={u} checked={selected.includes(u.id)} onToggle={toggle} />)}
                                    {meta.page < meta.pages && (
                                        <Button variant="ghost" className="w-full" onClick={() => loadUsers(meta.page + 1)} disabled={loading} data-testid="assign-load-more">
                                            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Load more
                                        </Button>
                                    )}
                                </div>
                            )}
                        </ScrollArea>
                        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                            <Button variant="outline" onClick={() => setConfirmBulk(true)} disabled={assigning || needsDepartments || meta.unassigned === 0} data-testid="assign-everyone-btn">
                                <Users className="mr-2 h-4 w-4" />Assign everyone in {scope === 'all' ? 'all departments' : 'these departments'} ({meta.unassigned})
                            </Button>
                            <Button onClick={assignSelected} disabled={assigning || selected.length === 0} data-testid="assign-selected-btn">
                                {assigning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
                                Assign Selected ({selected.length})
                            </Button>
                        </div>
                    </TabsContent>
                    <TabsContent value="assigned" className="mt-4">
                        <AssignedList assigned={assigned} onUnassign={unassign} />
                        {assignedTotal > assigned.length && (
                            <p className="mt-2 text-xs text-muted-foreground">Showing the latest {assigned.length} of {assignedTotal} assigned users</p>
                        )}
                    </TabsContent>
                </Tabs>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="assign-dialog-close">Close</Button>
                </DialogFooter>
                <BulkConfirm open={confirmBulk} onOpenChange={setConfirmBulk} count={meta.unassigned} label={scopeLabel} onConfirm={assignEveryone} />
            </DialogContent>
        </Dialog>
    );
};
